import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client/react";

import { RECORD_THEORY_VIEW } from "@/graphql/mutations/sessions";

type Tracked = {
  seconds: number;
  /** The student pressed "Supratau". */
  acknowledged: boolean;
  /** In the viewport right now. */
  visible: boolean;
  /** Open without the student asking. Always false now — nothing auto-opens. */
  auto: boolean;
  /** The end of the theory reached the viewport at least once. */
  reachedEnd: boolean;
  /** The first answer has been checked — reading time stops counting. */
  attempted: boolean;
  observer?: IntersectionObserver;
};

// Enough thresholds that scrolling through a long theory keeps firing.
const THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

/**
 * Measures engagement with an exercise's theory, honestly.
 *
 * "How long was it open" would mostly measure time on the slide — theory used
 * to open by itself. This counts only seconds where the theory is
 * actually in the viewport, in a focused tab, before the first answer is
 * checked — and records whether the student reached the end of it, which is the
 * part they have to do themselves.
 *
 * Best-effort: a lost report costs a data point, never the student's progress.
 */
export function useTheoryView(sessionId?: string) {
  const [recordTheoryView] = useMutation(RECORD_THEORY_VIEW);
  const tracked = useRef<Map<string, Tracked>>(new Map());

  const send = useCallback(
    (exerciseId: string, entry: Tracked) => {
      const seconds = Math.round(entry.seconds);
      if (!sessionId) return;
      if (seconds < 1 && !entry.reachedEnd && !entry.acknowledged) return;

      recordTheoryView({
        variables: {
          sessionId,
          exerciseId,
          seconds,
          autoOpened: entry.auto,
          scrolledToEnd: entry.reachedEnd,
          acknowledged: entry.acknowledged,
        },
      }).catch(() => {});
    },
    [recordTheoryView, sessionId]
  );

  const flush = useCallback(
    (exerciseId: string) => {
      const entry = tracked.current.get(exerciseId);
      if (!entry) return;
      entry.observer?.disconnect();
      tracked.current.delete(exerciseId);
      send(exerciseId, entry);
    },
    [send]
  );

  /**
   * Ref callback for the theory content element. Called with the node when the
   * theory is showing and with null when it is closed or unmounted.
   */
  const attach = useCallback(
    (exerciseId: string, node: HTMLElement | null, auto: boolean) => {
      if (!node) {
        flush(exerciseId);
        return;
      }
      if (tracked.current.has(exerciseId)) return;

      const entry: Tracked = {
        seconds: 0,
        acknowledged: false,
        visible: false,
        auto,
        reachedEnd: false,
        attempted: false,
      };

      entry.observer = new IntersectionObserver(
        (entries) => {
          const observed = entries[entries.length - 1];
          if (!observed) return;
          entry.visible = observed.isIntersecting;

          // The bottom edge inside the viewport means the student got to the end
          // of the text — for theory shorter than the screen that is immediate,
          // which is correct: there was nothing to scroll.
          const viewportBottom = observed.rootBounds?.bottom ?? window.innerHeight;
          if (observed.isIntersecting && observed.boundingClientRect.bottom <= viewportBottom + 4) {
            entry.reachedEnd = true;
          }
        },
        { threshold: THRESHOLDS }
      );
      entry.observer.observe(node);
      tracked.current.set(exerciseId, entry);
    },
    [flush]
  );

  /**
   * The student pressed "Supratau" — the strongest reading signal we get. Reported
   * immediately, because the panel closes right after and unmounts the element.
   */
  const markAcknowledged = useCallback(
    (exerciseId: string) => {
      const entry = tracked.current.get(exerciseId);
      if (!entry) return;
      entry.acknowledged = true;
      entry.attempted = true;
    },
    []
  );

  /** The student answered — stop counting this exercise as reading time. */
  const markAttempted = useCallback((exerciseId: string) => {
    const entry = tracked.current.get(exerciseId);
    if (entry) entry.attempted = true;
  }, []);

  // One ticker for everything on screen. Only accrues while the theory is
  // visible, the tab is focused, and no answer has been checked yet.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      tracked.current.forEach((entry) => {
        if (entry.visible && !entry.attempted) entry.seconds += 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Leaving the page mid-read still counts.
  useEffect(() => {
    const flushAll = () => Array.from(tracked.current.keys()).forEach(flush);
    window.addEventListener("pagehide", flushAll);
    return () => {
      window.removeEventListener("pagehide", flushAll);
      flushAll();
    };
  }, [flush]);

  return { attach, markAcknowledged, markAttempted };
}
